import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Notebook layout preference — currently just "is the note list hidden".
 *
 * Persisted (rather than component state) for the same reason `sidebarStore`
 * is: hiding the list is a working preference, not a per-visit gesture, and
 * having it snap back on every reload would make the mode not worth using.
 *
 * The flag is a PREFERENCE, not the rendered truth. The route applies it only
 * while a note is selected — see `notebook.tsx` — because hiding the list with
 * nothing open would leave the page with no list AND no note, and no way back.
 */

interface NotebookLayoutState {
  listHidden: boolean;
  setListHidden: (hidden: boolean) => void;
  toggleListHidden: () => void;
}

const DEFAULT_STATE: Pick<NotebookLayoutState, "listHidden"> = {
  listHidden: false,
};

export const useNotebookLayoutStore = create<NotebookLayoutState>()(
  persist(
    (set) => ({
      ...DEFAULT_STATE,
      setListHidden: (listHidden) => set({ listHidden }),
      toggleListHidden: () => set((s) => ({ listHidden: !s.listHidden })),
    }),
    {
      name: "clawcraft-notebook-layout",
      version: 1,
      migrate: () => ({ ...DEFAULT_STATE }),
    },
  ),
);
